import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, PlayIcon } from '@heroicons/react/24/outline';
import { loadReportQueue, reportQueuePath } from './reportQueueApi';

function formatStarted(evalStarted) {
  if (!evalStarted) return 'Unknown';
  const date = new Date(evalStarted);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
}

export default function ReportQueue() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setQueue(await loadReportQueue());
      setError(null);
    } catch (err) {
      console.error('Error loading report queue:', err);
      setError('Failed to load report queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
        <button
          type="button"
          onClick={fetchQueue}
          className="mt-4 inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          <ArrowPathIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Report Queue</h1>
          <p className="mt-2 text-sm text-gray-700">
            Cloud evals that haven&apos;t been scored yet, oldest first.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:items-center sm:gap-2">
          <button
            type="button"
            onClick={fetchQueue}
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <ArrowPathIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
            Refresh
          </button>
          {queue.length > 0 && (
            <button
              type="button"
              onClick={() => navigate(reportQueuePath(queue[0].evaluationId))}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500"
            >
              <PlayIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Start Queue
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-lg bg-gray-50 p-4">
        <p className="text-sm text-gray-700">
          Unscored evals: <span className="font-semibold text-gray-900">{queue.length}</span>
        </p>
      </div>

      {queue.length === 0 ? (
        <div className="mt-6 rounded-md bg-emerald-50 p-4 text-emerald-800">
          Queue is empty. Every eval has been scored.
        </div>
      ) : (
        <div className="mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Case</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Client</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Organization</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Started</th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4">
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {queue.map((item) => (
                <tr key={item.evaluationId}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">{item.caseNumber || 'N/A'}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{item.clientName || 'N/A'}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{item.organizationName || 'N/A'}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{formatStarted(item.evalStarted)}</td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium">
                    <button
                      type="button"
                      onClick={() => navigate(reportQueuePath(item.evaluationId))}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
